import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Edit2, Send, MapPin, CheckCircle, AlertCircle, Plus, ArrowLeft, LogIn } from 'lucide-react';
import { toast } from 'sonner';

export default function ReviewPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [reviewData, setReviewData] = useState(null);
  const [step, setStep] = useState('review'); // review | login | pincode | address | submitting
  const [pincode, setPincode] = useState('');
  const [pincodeResult, setPincodeResult] = useState(null);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [newAddress, setNewAddress] = useState({ label: 'home', name: '', phone: '', line1: '', line2: '', city: '', state: '', pincode: '' });
  const [submitting, setSubmitting] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState('');

  useEffect(() => {
    const raw = sessionStorage.getItem('besq_review');
    if (!raw) { navigate('/categories'); return; }
    setReviewData(JSON.parse(raw));
  }, [navigate]);

  useEffect(() => {
    if (isAuthenticated && (step === 'address' || step === 'pincode')) {
      api.get('/addresses').then((res) => {
        const addrs = res.data.data || [];
        setAddresses(addrs);
        const def = addrs.find((a) => a.is_default);
        if (def) setSelectedAddressId(def.id);
      }).catch(() => {});
    }
  }, [isAuthenticated, step]);

  const handleProceed = () => {
    if (!isAuthenticated) {
      sessionStorage.setItem('besq_return', '/review');
      navigate('/login');
      return;
    }
    setStep('pincode');
  };

  const checkPincode = async () => {
    if (!pincode || pincode.length < 5) { toast.error('Enter a valid pincode'); return; }
    setPincodeLoading(true);
    try {
      const res = await api.get(`/pincode/check?pincode=${pincode}`);
      const data = res.data.data;
      setPincodeResult(data);
      if (data.serviceable) {
        let city = '';
        let state = '';
        try {
          const pinRes = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
          const pinData = await pinRes.json();
          if (Array.isArray(pinData) && pinData[0]?.Status === 'Success' && pinData[0].PostOffice?.length) {
            const po = pinData[0].PostOffice[0];
            city = po.District || '';
            state = po.State || '';
          }
        } catch { /* silent fallback */ }
        setNewAddress((p) => ({ ...p, pincode, city: city || p.city, state: state || p.state }));
        // Stay on this page — user picks store via inline cards (handles both single and multi-store cases)
        setSelectedBranchId('');
      }
    } catch { toast.error('Failed to check pincode'); }
    finally { setPincodeLoading(false); }
  };

  const handleAddAddress = async () => {
    const addr = { ...newAddress, pincode: pincode || newAddress.pincode, is_default: addresses.length === 0 };
    if (!addr.name || !addr.phone || !addr.line1 || !addr.city || !addr.state || !addr.pincode) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      const res = await api.post('/addresses', addr);
      const created = res.data.data;
      setAddresses((p) => [...p, created]);
      setSelectedAddressId(created.id);
      setShowAddAddress(false);
      toast.success('Address added');
    } catch { toast.error('Failed to add address'); }
  };

  const handleSubmitOrder = async () => {
    if (!selectedAddressId) { toast.error('Please select an address'); return; }
    setSubmitting(true);
    try {
      await api.post('/orders/customization', {
        category_id: reviewData.categoryId,
        style: reviewData.style,
        preferences: reviewData.preferences,
        measurement: { ...reviewData.measurement, address_id: selectedAddressId },
        pincode: pincode || '',
        branch_id: selectedBranchId || undefined,
      });
      sessionStorage.removeItem('besq_review');
      sessionStorage.removeItem('besq_customization');
      navigate('/order-success');
    } catch { toast.error('Failed to submit order'); }
    finally { setSubmitting(false); }
  };

  if (!reviewData) return null;

  const measurementLabels = { pickup_measurement: 'Pickup Measurement', saved_measurement: 'Saved Measurement', reference_garment: 'Reference Garment' };

  const styleCount = Object.keys(reviewData.style || {}).length;
  const prefsCount = Object.entries(reviewData.preferences || {}).filter(([, v]) => v).length;

  const flowSteps = [
    { key: 'review', label: 'Review' },
    { key: 'pincode', label: 'Serviceability' },
    { key: 'address', label: 'Delivery Address' },
  ];
  const flowIndex = flowSteps.findIndex((s) => s.key === step);

  return (
    <div className="bg-background" data-testid="review-page">
      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr]">
        {/* ============ SIDEBAR ============ */}
        <aside className="hidden lg:block sticky top-[72px] self-start h-[calc(100vh-72px)] overflow-y-auto border-r border-border py-14 px-11" data-testid="review-sidebar">
          <button onClick={() => navigate(`/categories/${reviewData.categorySlug}`)} className="inline-flex items-center gap-1.5 uppercase tracking-luxe text-[11px] font-semibold text-ink-muted hover:text-ink mb-8" data-testid="back-to-customization">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to customization
          </button>
          <div className="eyebrow mb-3">Almost There</div>
          <h1 className="font-display font-light text-ink leading-none tracking-[-0.015em] mb-10" style={{ fontSize: 44 }}>Review — {reviewData.categoryName}</h1>

          {/* Flow progress */}
          <ul className="border-y border-border">
            {flowSteps.map((s, i) => {
              const isActive = i === flowIndex;
              const isDone = i < flowIndex;
              return (
                <li key={s.key} className="relative border-t border-border first:border-t-0 py-4 flex items-center gap-4">
                  {isActive && <span className="absolute -left-11 top-4 bottom-4 w-[2px] bg-ink" />}
                  <span className="font-display text-gold" style={{ fontSize: 22, width: 32 }}>0{i + 1}{isDone && ' ✓'}</span>
                  <span className={`uppercase tracking-luxe text-[13px] ${isActive ? 'font-semibold text-ink' : isDone ? 'font-medium text-ink' : 'font-medium text-ink-muted'}`}>{s.label}</span>
                </li>
              );
            })}
          </ul>

          {/* Order Summary snapshot */}
          <div className="mt-10 pt-8 border-t border-border space-y-4">
            <div className="uppercase tracking-luxe text-[10px] font-semibold text-ink-muted mb-3">Order Summary</div>
            <div className="flex justify-between text-[13px]"><span className="text-ink-muted">Category</span><span className="text-ink font-medium">{reviewData.categoryName}</span></div>
            <div className="flex justify-between text-[13px]"><span className="text-ink-muted">Style choices</span><span className="text-ink font-medium">{styleCount}</span></div>
            <div className="flex justify-between text-[13px]"><span className="text-ink-muted">Preferences</span><span className="text-ink font-medium">{prefsCount}</span></div>
            <div className="flex justify-between text-[13px]"><span className="text-ink-muted">Measurement</span><span className="text-ink font-medium">{measurementLabels[reviewData.measurement.method] || '-'}</span></div>
            {isAuthenticated && <div className="flex justify-between text-[13px] pt-2 border-t border-border"><span className="text-ink-muted">Customer</span><span className="text-ink font-medium">{user?.name}</span></div>}
          </div>
        </aside>

        {/* ============ MAIN ============ */}
        <main className="px-5 lg:px-16 py-10 lg:py-16">
          {/* Mobile header (sidebar hidden) */}
          <div className="lg:hidden mb-8">
            <button onClick={() => navigate(`/categories/${reviewData.categorySlug}`)} className="inline-flex items-center gap-1.5 uppercase tracking-luxe text-[11px] font-semibold text-ink-muted hover:text-ink mb-4">
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            <div className="eyebrow mb-2">Almost There</div>
            <h1 className="font-display font-light text-ink leading-none tracking-[-0.015em]" style={{ fontSize: 32 }}>Review — {reviewData.categoryName}</h1>
          </div>

      {step === 'review' && (
        <>
          {/* Style Summary */}
          <Section title="Style" onEdit={() => navigate(`/categories/${reviewData.categorySlug}?step=0`)}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(reviewData.style).map(([key, sel]) => (
                <div key={key} className="border border-border p-2 bg-background">
                  <div className="aspect-square bg-secondary overflow-hidden mb-2">
                    {sel?.value && <img src={sel.value.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${sel.value}` : sel.value} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <p className="text-[10px] uppercase tracking-luxe font-semibold text-ink-muted">{key.replace(/_/g, ' ')}</p>
                  <p className="text-[13px] text-ink font-medium truncate">{sel?.label || '-'}</p>
                </div>
              ))}
            </div>
          </Section>

          {/* Preferences Summary */}
          <Section title="Preferences" onEdit={() => navigate(`/categories/${reviewData.categorySlug}?step=1`)}>
            <div className="space-y-2">
              {Object.entries(reviewData.preferences).filter(([, v]) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0)).map(([key, val]) => {
                // Normalise to a readable label — handle arrays (multiselect), objects (preset), strings, booleans
                let display;
                if (Array.isArray(val)) display = val.map(v => String(v).replace(/_/g, ' ')).join(', ');
                else if (typeof val === 'object' && val !== null) display = String(val.value ?? val.label ?? '').replace(/_/g, ' ');
                else display = String(val).replace(/_/g, ' ');
                return (
                  <div key={key} className="flex justify-between text-[14px] border-b border-border/60 pb-2 last:border-0 last:pb-0">
                    <span className="text-ink-muted capitalize">{key.replace(/_/g, ' ')}</span>
                    <span className="text-ink font-medium capitalize">{display}</span>
                  </div>
                );
              })}
              {Object.keys(reviewData.preferences).length === 0 && <p className="text-[14px] text-ink-muted">No preferences set</p>}
            </div>
          </Section>

          {/* Measurement Summary */}
          <Section title="Measurement" onEdit={() => navigate(`/categories/${reviewData.categorySlug}?step=2`)}>
            <p className="font-display text-ink" style={{ fontSize: 20 }}>{measurementLabels[reviewData.measurement.method] || '-'}</p>
            {reviewData.measurement.profile_name && <p className="text-[13px] text-ink-muted mt-1">Profile: {reviewData.measurement.profile_name}</p>}
          </Section>

          <div className="mt-12">
            <button
              onClick={handleProceed}
              data-testid="proceed-btn"
              className="w-full inline-flex items-center justify-center gap-3 border border-ink bg-ink text-background hover:bg-transparent hover:text-ink uppercase tracking-luxe font-medium py-4 transition-all duration-300"
              style={{ fontSize: 13 }}
            >
              {isAuthenticated ? <><MapPin className="w-4 h-4" /> Check Serviceability</> : <><LogIn className="w-4 h-4" /> Sign in to Proceed</>}
            </button>
          </div>
        </>
      )}

      {step === 'pincode' && (
        <div className="space-y-6" data-testid="pincode-step">
          <div>
            <div className="eyebrow mb-3">Delivery</div>
            <h2 className="font-display text-ink" style={{ fontSize: 32 }}>Check Serviceability</h2>
            <p className="text-ink/70 mt-2" style={{ fontSize: 15 }}>Enter your pincode to see which of our stores deliver to your area.</p>
          </div>
          <div className="flex gap-3">
            <Input value={pincode} onChange={(e) => { setPincode(e.target.value.replace(/\D/g, '')); setPincodeResult(null); setSelectedBranchId(''); }} placeholder="Enter pincode" maxLength={6} className="max-w-xs rounded-none border-t-0 border-l-0 border-r-0 border-b border-ink/20 focus-visible:ring-0 focus-visible:border-gold px-0 text-[16px]" data-testid="pincode-input" />
            <button onClick={checkPincode} disabled={pincodeLoading} className="inline-flex items-center gap-2 border border-ink bg-ink text-background hover:bg-transparent hover:text-ink uppercase tracking-luxe font-medium px-6 py-3 transition-all duration-300 disabled:opacity-50" style={{ fontSize: 12 }} data-testid="check-pincode-btn">
              {pincodeLoading ? 'Checking…' : 'Check'}
            </button>
          </div>
          {pincodeResult && !pincodeResult.serviceable && (
            <div className="flex items-start gap-2.5 p-4 border border-destructive/30 bg-destructive/5 text-[13px] text-destructive" data-testid="pincode-not-serviceable">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> Sorry, we don&apos;t deliver to {pincode} yet. Try a different pincode.
            </div>
          )}
          {pincodeResult?.serviceable && (pincodeResult.branches || []).length > 0 && (
            <div className="space-y-4" data-testid="store-picker">
              <div className="flex items-start gap-2.5 p-4 border border-gold/40 bg-gold/5 text-[13px] text-ink">
                <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-gold" /> Great! {pincodeResult.branches.length} store{pincodeResult.branches.length > 1 ? 's' : ''} serve{pincodeResult.branches.length === 1 ? 's' : ''} {pincode}. Pick where you&apos;d like your order processed.
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {pincodeResult.branches.map((b) => (
                  <button
                    key={b.id}
                    onClick={() => setSelectedBranchId(b.id)}
                    className={`text-left p-5 border-2 transition-all ${selectedBranchId === b.id ? 'border-ink bg-secondary/50' : 'border-border hover:border-ink/50'}`}
                    data-testid={`branch-${b.id}`}
                  >
                    <p className="font-display text-ink mb-1" style={{ fontSize: 18 }}>{b.name}</p>
                    {b.address && <p className="text-[12px] text-ink-muted">{b.address}</p>}
                    {b.phone && <p className="text-[12px] text-ink-muted mt-0.5">{b.phone}</p>}
                    {selectedBranchId === b.id && <p className="uppercase tracking-luxe text-[10px] text-ink font-semibold mt-2">✓ Selected</p>}
                  </button>
                ))}
              </div>
              <div className="flex items-center justify-between pt-2">
                <button onClick={() => setStep('review')} className="inline-flex items-center gap-1.5 uppercase tracking-luxe text-[11px] font-semibold text-ink-muted hover:text-ink" data-testid="back-to-review-btn">
                  <ArrowLeft className="w-3.5 h-3.5" /> Back
                </button>
                <button disabled={!selectedBranchId} onClick={() => setStep('address')} className="inline-flex items-center gap-2 border border-ink bg-ink text-background hover:bg-transparent hover:text-ink uppercase tracking-luxe font-medium px-8 py-3 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-ink disabled:hover:text-background" style={{ fontSize: 12 }} data-testid="confirm-branch-btn">
                  Continue →
                </button>
              </div>
            </div>
          )}
          {!pincodeResult && (
            <button onClick={() => setStep('review')} className="inline-flex items-center gap-1.5 uppercase tracking-luxe text-[11px] font-semibold text-ink-muted hover:text-ink" data-testid="back-to-review-btn">
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
          )}
        </div>
      )}

      {step === 'address' && (
        <div className="space-y-6" data-testid="address-step">
          <div>
            <div className="eyebrow mb-3">Delivery Address</div>
            <h2 className="font-display text-ink" style={{ fontSize: 32 }}>Where should we deliver?</h2>
          </div>

          {addresses.length > 0 && !showAddAddress && (
            <div className="space-y-3">
              {addresses.map((addr) => (
                <button
                  key={addr.id}
                  onClick={() => setSelectedAddressId(addr.id)}
                  className={`w-full text-left p-5 border-2 transition-all ${selectedAddressId === addr.id ? 'border-ink bg-secondary/50' : 'border-border hover:border-ink/50'}`}
                  data-testid={`address-${addr.id}`}
                >
                  <p className="uppercase tracking-luxe text-[10px] font-semibold text-ink-muted mb-1">{addr.label}</p>
                  <p className="font-display text-ink" style={{ fontSize: 17 }}>{addr.name} · {addr.phone}</p>
                  <p className="text-[13px] text-ink/75 mt-1">{addr.line1}{addr.line2 ? `, ${addr.line2}` : ''}</p>
                  <p className="text-[13px] text-ink/75">{addr.city}, {addr.state} - {addr.pincode}</p>
                </button>
              ))}
            </div>
          )}

          {!showAddAddress && (
            <button onClick={() => setShowAddAddress(true)} className="w-full inline-flex items-center justify-center gap-2 border border-ink text-ink hover:bg-ink hover:text-background uppercase tracking-luxe font-medium py-3.5 transition-all duration-300" style={{ fontSize: 12 }} data-testid="add-new-address-btn">
              <Plus className="w-4 h-4" /> Add New Address
            </button>
          )}

          {showAddAddress && (
            <div className="border border-border p-5 space-y-4" data-testid="new-address-form">
              <h3 className="font-display text-ink" style={{ fontSize: 20 }}>New Address</h3>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="uppercase tracking-luxe text-[10px] font-semibold text-ink-muted mb-1 block">Name *</Label><Input value={newAddress.name} onChange={(e) => setNewAddress({ ...newAddress, name: e.target.value })} placeholder="Full name" className="rounded-none border-t-0 border-l-0 border-r-0 border-b border-ink/20 focus-visible:ring-0 focus-visible:border-gold px-0" data-testid="addr-name" /></div>
                <div><Label className="uppercase tracking-luxe text-[10px] font-semibold text-ink-muted mb-1 block">Phone *</Label><Input value={newAddress.phone} onChange={(e) => setNewAddress({ ...newAddress, phone: e.target.value })} placeholder="+91..." className="rounded-none border-t-0 border-l-0 border-r-0 border-b border-ink/20 focus-visible:ring-0 focus-visible:border-gold px-0" data-testid="addr-phone" /></div>
              </div>
              <div><Label className="uppercase tracking-luxe text-[10px] font-semibold text-ink-muted mb-1 block">Address Line 1 *</Label><Input value={newAddress.line1} onChange={(e) => setNewAddress({ ...newAddress, line1: e.target.value })} placeholder="House, Building, Street" className="rounded-none border-t-0 border-l-0 border-r-0 border-b border-ink/20 focus-visible:ring-0 focus-visible:border-gold px-0" data-testid="addr-line1" /></div>
              <div><Label className="uppercase tracking-luxe text-[10px] font-semibold text-ink-muted mb-1 block">Address Line 2</Label><Input value={newAddress.line2} onChange={(e) => setNewAddress({ ...newAddress, line2: e.target.value })} placeholder="Landmark (optional)" className="rounded-none border-t-0 border-l-0 border-r-0 border-b border-ink/20 focus-visible:ring-0 focus-visible:border-gold px-0" data-testid="addr-line2" /></div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label className="uppercase tracking-luxe text-[10px] font-semibold text-ink-muted mb-1 block">City *</Label><Input value={newAddress.city} onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })} className="rounded-none border-t-0 border-l-0 border-r-0 border-b border-ink/20 focus-visible:ring-0 focus-visible:border-gold px-0" data-testid="addr-city" /></div>
                <div><Label className="uppercase tracking-luxe text-[10px] font-semibold text-ink-muted mb-1 block">State *</Label><Input value={newAddress.state} onChange={(e) => setNewAddress({ ...newAddress, state: e.target.value })} className="rounded-none border-t-0 border-l-0 border-r-0 border-b border-ink/20 focus-visible:ring-0 focus-visible:border-gold px-0" data-testid="addr-state" /></div>
                <div><Label className="uppercase tracking-luxe text-[10px] font-semibold text-ink-muted mb-1 block">Pincode *</Label><Input value={newAddress.pincode || pincode} onChange={(e) => setNewAddress({ ...newAddress, pincode: e.target.value })} className="rounded-none border-t-0 border-l-0 border-r-0 border-b border-ink/20 focus-visible:ring-0 focus-visible:border-gold px-0" data-testid="addr-pincode" /></div>
              </div>
              <div className="flex gap-3">
                <button onClick={handleAddAddress} className="inline-flex items-center justify-center gap-2 border border-ink bg-ink text-background hover:bg-transparent hover:text-ink uppercase tracking-luxe font-medium px-8 py-3 transition-all duration-300 flex-1" style={{ fontSize: 12 }} data-testid="save-address-btn">Save Address</button>
                <button onClick={() => setShowAddAddress(false)} className="inline-flex items-center gap-2 border border-ink text-ink hover:bg-ink hover:text-background uppercase tracking-luxe font-medium px-8 py-3 transition-all duration-300" style={{ fontSize: 12 }}>Cancel</button>
              </div>
            </div>
          )}

          <div className="flex gap-3 mt-8">
            <button onClick={() => setStep('pincode')} className="inline-flex items-center gap-2 border border-ink text-ink hover:bg-ink hover:text-background uppercase tracking-luxe font-medium px-8 py-3.5 transition-all duration-300" style={{ fontSize: 12 }} data-testid="back-pincode-btn"><ArrowLeft className="w-4 h-4" /> Back</button>
            <button onClick={handleSubmitOrder} disabled={submitting || !selectedAddressId} className="flex-1 inline-flex items-center justify-center gap-2 border border-ink bg-ink text-background hover:bg-transparent hover:text-ink uppercase tracking-luxe font-medium px-8 py-3.5 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-ink disabled:hover:text-background" style={{ fontSize: 12 }} data-testid="request-order-btn">
              <Send className="w-4 h-4" /> {submitting ? 'Submitting…' : 'Request Order'}
            </button>
          </div>
        </div>
      )}
        </main>
      </div>
    </div>
  );
}

function Section({ title, onEdit, children }) {
  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-4">
        <h3 className="uppercase tracking-luxe text-[12px] font-semibold text-ink">{title}</h3>
        <button onClick={onEdit} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-luxe font-semibold text-ink-muted hover:text-gold" data-testid={`edit-${title.toLowerCase()}`}>
          <Edit2 className="w-3 h-3" />Edit
        </button>
      </div>
      <div className="border border-border p-6 bg-cream-2/40">{children}</div>
    </div>
  );
}
